/**
 * Noodmude Shipping Upsell Widget
 * Vanilla JS — no framework dependencies
 */

'use strict';

// ---------------------------------------------------------------------------
// Logging — prefix all messages for easy filtering in DevTools
// ---------------------------------------------------------------------------

const _LOG_PREFIX = '[noodmude-upsell]';
function _log(/* ...args */) {
  console.log.apply(console, [_LOG_PREFIX].concat(Array.prototype.slice.call(arguments)));
}
function _warn(/* ...args */) {
  console.warn.apply(console, [_LOG_PREFIX].concat(Array.prototype.slice.call(arguments)));
}

// ---------------------------------------------------------------------------
// Types (JSDoc)
// ---------------------------------------------------------------------------

/**
 * @typedef {{ vendor: string, id: number, key: string, grams: number, quantity: number }} CartItem
 * @typedef {{ vendor: string, items: CartItem[], totalWeightG: number, maxItemPrice: number }} VendorGroup
 * @typedef {{ groups: Record<string, VendorGroup>, cartItemIds: number[] }} CartByVendor
 *
 * @typedef {{ id: string, variantId: string, title: string, handle: string, url: string,
 *             price: string, currencyCode: string, image: { src: string, alt: string },
 *             weightG: number }} Product
 *
 * @typedef {{ currentPrice: string|null, headroomG: number, nextBandPrice: string|null,
 *             isTopBand: boolean, currency: string }} VendorHeadroom
 */

// ---------------------------------------------------------------------------
// Market normalization — Shopify market handles ("EUROPEAN-UNION") differ
// from the codes the shipping proxy expects ("EU"). Map known handles here.
// ---------------------------------------------------------------------------

/** @param {string} raw @returns {string} */
function _normalizeMarket(raw) {
  if (!raw) return 'GB';
  var v = String(raw).toUpperCase();
  // Already a 2-letter code — pass through
  if (v.length === 2) return v;
  // Known Shopify market handles
  var map = {
    'EUROPEAN-UNION': 'EU',
    'EUROPEANUNION': 'EU',
    'EUROPE': 'EU',
    'UNITED-KINGDOM': 'GB',
    'UNITEDKINGDOM': 'GB',
    'UK': 'GB',
    'BRITAIN': 'GB',
    'GREAT-BRITAIN': 'GB',
  };
  if (map[v]) return map[v];
  _warn('Unknown market handle:', raw, '— defaulting to GB. Add a mapping in _normalizeMarket if this is wrong.');
  return 'GB';
}

// ---------------------------------------------------------------------------
// Vendor name normalization — case/accent/whitespace insensitive. Lets
// "Léa Studio", "Lea Studio  ", and "LEA STUDIO" compare equal so a Shopify
// product vendor and a shipping-profile zone name match even if punctuation
// and accents drift between them.
// ---------------------------------------------------------------------------

/** @param {string} name @returns {string} */
function _normalizeVendorName(name) {
  if (!name) return '';
  var s = String(name);
  // NFD splits "é" into "e" + combining accent; strip the combining marks.
  if (s.normalize) s = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  return s.trim().toLowerCase();
}

// ---------------------------------------------------------------------------
// Part 4A: Cart fetch and vendor grouping
// ---------------------------------------------------------------------------

/**
 * @returns {Promise<CartByVendor>}
 */
async function getCartByVendor() {
  try {
    const res = await fetch('/cart.js');
    if (!res.ok) throw new Error('Cart fetch HTTP ' + res.status);
    const cart = await res.json();

    /** @type {Record<string, VendorGroup>} */
    const groups = {};
    /** @type {number[]} */
    const cartItemIds = [];

    for (const item of cart.items) {
      if (!item.vendor) continue;
      const vendor = item.vendor;
      cartItemIds.push(item.product_id);

      if (!groups[vendor]) {
        groups[vendor] = { vendor, items: [], totalWeightG: 0, maxItemPrice: 0 };
      }
      groups[vendor].items.push(item);
      groups[vendor].totalWeightG += (item.grams || 0) * item.quantity;
      // item.price is in cents/pence (subunits)
      var itemPrice = (item.price || 0) / 100;
      if (itemPrice > groups[vendor].maxItemPrice) groups[vendor].maxItemPrice = itemPrice;
    }

    _log('Cart parsed:', Object.keys(groups).length, 'vendors,', cart.items.length, 'items, excludeIds:', cartItemIds);
    return { groups, cartItemIds };
  } catch (err) {
    _warn('Cart fetch failed:', err);
    return { groups: {}, cartItemIds: [] };
  }
}

// ---------------------------------------------------------------------------
// Part 4B: Shipping headroom fetch (no debounce — orchestrator handles timing)
// ---------------------------------------------------------------------------

/**
 * @param {Record<string, VendorGroup>} vendorGroups
 * @param {string} market
 * @returns {Promise<{ vendors: Record<string, VendorHeadroom> }|null>}
 */
async function fetchShippingHeadroom(vendorGroups, market) {
  const vendors = Object.keys(vendorGroups);
  /** @type {Record<string, number>} */
  const weights = {};
  for (const v of vendors) weights[v] = vendorGroups[v].totalWeightG;

  _log('Fetching headroom for', vendors.join(', '), '| market:', market, '| weights:', JSON.stringify(weights));

  try {
    const res = await fetch(window.noodmude.proxyUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ vendors, market, weights }),
    });
    if (!res.ok) throw new Error('HTTP ' + res.status);
    const data = await res.json();
    _log('Headroom response:', JSON.stringify(data));
    return data;
  } catch (err) {
    _warn('Headroom fetch failed:', err);
    return null;
  }
}

// ---------------------------------------------------------------------------
// Part 4C: Product recommendations fetch
// ---------------------------------------------------------------------------

/** @type {Map<string, Product[]>} */
const _productCache = new Map();

/**
 * @param {string} vendor
 * @param {number} maxWeightG
 * @param {number[]} excludeIds
 * @param {number} [maxPrice] — price ceiling in major currency units (0 or falsy = no cap)
 * @param {string} [currencyCode] — currency code to tag products with (from headroom response)
 * @returns {Promise<Product[]>}
 */
async function fetchEligibleProducts(vendor, maxWeightG, excludeIds, maxPrice, currencyCode) {
  const cacheKey = vendor + '__' + maxWeightG;
  let rawProducts;

  if (_productCache.has(cacheKey)) {
    rawProducts = _productCache.get(cacheKey);
    _log('Product cache HIT for', cacheKey, '— raw:', rawProducts.length);
  } else {
    _log('Product cache MISS for', cacheKey, '— fetching /products.json');
    try {
      const params = new URLSearchParams({ vendor: vendor, limit: '50' });
      const res = await fetch('/products.json?' + params);
      if (!res.ok) throw new Error('Products API HTTP ' + res.status);
      const json = await res.json();

      rawProducts = [];

      for (const node of json.products || []) {
        // Only include products from the requested vendor (API doesn't filter).
        // Use accent-insensitive comparison (handles "Léa" vs "Lea" etc.)
        if (_normalizeVendorName(node.vendor || '') !== _normalizeVendorName(vendor)) continue;

        var variants = node.variants || [];
        if (!variants.length) continue;

        // Pick the first variant that is:
        //  - in stock (available !== false — Shopify sometimes omits the field)
        //  - light enough to fit in the remaining weight budget
        // This handles products with size/colour variants that differ in weight
        // and price, and automatically skips sold-out variants.
        var chosenVariant = null;
        for (var vi = 0; vi < variants.length; vi++) {
          var v = variants[vi];
          var isAvailable = v.available !== false; // undefined → treat as available
          var vWeight = v.grams || 0;
          if (isAvailable && vWeight <= maxWeightG) { chosenVariant = v; break; }
        }

        if (!chosenVariant) {
          // Figure out which constraint rejected every variant so the log is
          // actionable — sold out vs too heavy are very different problems.
          var anyAvailable = variants.some(function (v) { return v.available !== false; });
          if (!anyAvailable) {
            _log('  Skipping', node.title, '— all variants sold out');
          } else {
            var lightestAvailable = Math.min.apply(null, variants
              .filter(function (v) { return v.available !== false; })
              .map(function (v) { return v.grams || 0; }));
            _log('  Skipping', node.title, '— lightest available variant', lightestAvailable, 'g exceeds headroom', maxWeightG, 'g');
          }
          continue;
        }

        var image = (node.images || [])[0];
        rawProducts.push({
          id: String(node.id),
          variantId: String(chosenVariant.id),
          title: node.title,
          handle: node.handle,
          url: '/products/' + node.handle + '?variant=' + chosenVariant.id,
          price: chosenVariant.price,
          currencyCode: currencyCode || 'GBP',
          image: {
            src: image ? (image.src || '') : '',
            alt: image ? (image.alt || node.title) : node.title,
          },
          weightG: chosenVariant.grams || 0,
        });
      }

      _productCache.set(cacheKey, rawProducts);
      _log('Cached', rawProducts.length, 'products for', cacheKey);
    } catch (err) {
      _warn('Product fetch failed for', vendor, ':', err);
      return [];
    }
  }

  // Apply exclusions + price cap after cache (cache stores unfiltered results)
  var filtered = rawProducts.filter(function (p) { return !excludeIds.includes(Number(p.id)); });
  if (maxPrice) {
    filtered = filtered.filter(function (p) { return parseFloat(p.price) <= maxPrice; });
  }
  var result = filtered.slice(0, 3);

  _log('Eligible for', vendor, ':', result.length, 'of', rawProducts.length,
    '(excluded:', excludeIds.length, ', priceCap:', maxPrice || 'none', ')');
  if (result.length > 0) {
    _log('  →', result.map(function (p) { return p.title + ' (' + p.price + ')'; }).join(', '));
  }
  return result;
}

// ---------------------------------------------------------------------------
// Part 4D: Widget render
// ---------------------------------------------------------------------------

/** @param {string} code @returns {string} */
function _currencySymbol(code) {
  if (code === 'GBP') return '\u00a3';
  if (code === 'EUR') return '\u20ac';
  return code + ' ';
}

/**
 * @param {string} vendor
 * @param {Product[]} products
 * @param {string} currentPrice
 * @param {string} currency
 * @returns {string}
 */
function renderUpsellWidget(vendor, products, currentPrice, currency) {
  var headingTemplate = (window.noodmude && window.noodmude.upsellHeading || 'Add more from [vendor] \u2014 shipping already covered')
    .replace('[vendor]', _escapeHtml(vendor));
  var subheading = _escapeHtml(window.noodmude && window.noodmude.upsellSubheading || 'These items can join the same shipment at no extra cost.');

  var slides = products.map(function (p, i) {
    return '\n    <div class="noodmude-upsell__slide' + (i === 0 ? ' is-active' : '') + '" data-slide-index="' + i + '">' +
      '\n      <a href="' + p.url + '" class="noodmude-upsell__card-image-link">' +
      '\n        <img class="noodmude-upsell__card-image" src="' + p.image.src + '" alt="' + _escapeHtml(p.image.alt) + '" loading="lazy" />' +
      '\n      </a>' +
      '\n      <div class="noodmude-upsell__card-info">' +
      '\n        <p class="noodmude-upsell__card-title">' + _escapeHtml(p.title) + '</p>' +
      '\n        <p class="noodmude-upsell__card-price">' + _currencySymbol(p.currencyCode) + parseFloat(p.price).toFixed(2) + '</p>' +
      '\n        <button type="button" class="noodmude-upsell__add-btn" data-variant-id="' + _escapeHtml(p.variantId) + '" data-vendor="' + _escapeHtml(vendor) + '" aria-label="Add ' + _escapeHtml(p.title) + ' to cart">Add to cart</button>' +
      '\n      </div>' +
      '\n    </div>';
  }).join('');

  var nav = products.length > 1 ?
    '\n    <div class="noodmude-upsell__nav">' +
    '\n      <button type="button" class="noodmude-upsell__prev" aria-label="Previous suggestion">&#8249;</button>' +
    '\n      <span class="noodmude-upsell__counter">1 / ' + products.length + '</span>' +
    '\n      <button type="button" class="noodmude-upsell__next" aria-label="Next suggestion">&#8250;</button>' +
    '\n    </div>' : '';

  return '<div class="noodmude-upsell" data-upsell-widget="' + _escapeHtml(vendor) + '">' +
    '\n      <div class="noodmude-upsell__header">' +
    '\n        <p class="noodmude-upsell__heading">' + headingTemplate + '</p>' +
    '\n        <button type="button" class="noodmude-upsell__dismiss" data-dismiss-vendor="' + _escapeHtml(vendor) + '" aria-label="Dismiss">&#215;</button>' +
    '\n      </div>' +
    '\n      <p class="noodmude-upsell__subheading">' + subheading + '</p>' +
    '\n      <div class="noodmude-upsell__slide-area">' + slides + '</div>' +
    nav +
    '\n    </div>';
}

/** @param {string} str @returns {string} */
function _escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// ---------------------------------------------------------------------------
// Part 4E: Interactions (add to cart, dismiss, slider nav)
// ---------------------------------------------------------------------------

/** @type {Set<string>} */
var _inflight = new Set();

var _dismissed;
try {
  _dismissed = new Set(JSON.parse(sessionStorage.getItem('noodmude_dismissed') || '[]'));
} catch (e) {
  _dismissed = new Set();
}

function _dismissVendor(vendor) {
  _dismissed.add(vendor);
  sessionStorage.setItem('noodmude_dismissed', JSON.stringify(Array.from(_dismissed)));
  var widget = document.querySelector('[data-upsell-widget="' + CSS.escape(vendor) + '"]');
  if (widget) widget.remove();
  _log('Dismissed vendor:', vendor);
}

function _navigateSlider(widget, direction) {
  if (!widget) return;
  var slides = widget.querySelectorAll('[data-slide-index]');
  var counter = widget.querySelector('.noodmude-upsell__counter');
  var total = slides.length;
  var current = Array.from(slides).findIndex(function (s) { return s.classList.contains('is-active'); });
  if (current < 0) current = 0;
  slides[current].classList.remove('is-active');
  current = ((current + direction) + total) % total;
  slides[current].classList.add('is-active');
  if (counter) counter.textContent = (current + 1) + ' / ' + total;
}

function initUpsellCartButtons() {
  // Handler registered once in _bootstrap via event delegation — nothing to do here
}

/** @param {MouseEvent} e */
async function _handleUpsellClick(e) {
  // Dismiss
  var dismissBtn = e.target.closest('[data-dismiss-vendor]');
  if (dismissBtn) { _dismissVendor(dismissBtn.dataset.dismissVendor); return; }

  // Slider prev/next
  var prevBtn = e.target.closest('.noodmude-upsell__prev');
  if (prevBtn) { _navigateSlider(prevBtn.closest('[data-upsell-widget]'), -1); return; }
  var nextBtn = e.target.closest('.noodmude-upsell__next');
  if (nextBtn) { _navigateSlider(nextBtn.closest('[data-upsell-widget]'), 1); return; }

  // Add to cart
  var btn = e.target.closest('[data-variant-id].noodmude-upsell__add-btn');
  if (!btn) return;

  var variantId = btn.dataset.variantId;
  if (_inflight.has(variantId)) return;

  _inflight.add(variantId);
  btn.classList.add('noodmude-upsell__add-btn--loading');
  btn.disabled = true;
  _log('Adding to cart: variant', variantId);

  try {
    var sections = 'drawer-cart-items,drawer-cart-footer,cart-item-count,cart-live-region';
    var res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: Number(variantId),
        quantity: 1,
        sections: sections,
        sections_url: window.location.pathname,
      }),
    });

    if (!res.ok) throw new Error('HTTP ' + res.status);

    var data = await res.json();
    _log('Add to cart OK:', data.title || data.product_title || variantId);

    btn.classList.remove('noodmude-upsell__add-btn--loading');
    btn.disabled = false;

    // Tell the Exhibit theme to update the cart drawer via Alpine.morph
    window.dispatchEvent(new CustomEvent('switch:cart:afteradditem', {
      detail: { response: data },
    }));

    // Also open the cart drawer if it isn't already open
    if (typeof Alpine !== 'undefined' && Alpine.store('modals')) {
      Alpine.store('modals').open('cart');
    }

    document.dispatchEvent(new CustomEvent('noodmude:cart:updated'));
  } catch (err) {
    _warn('Add to cart failed:', err);
    btn.classList.remove('noodmude-upsell__add-btn--loading');
    btn.classList.add('noodmude-upsell__add-btn--error');
    btn.disabled = false;

    setTimeout(function () {
      btn.classList.remove('noodmude-upsell__add-btn--error');
    }, 2000);
  } finally {
    _inflight.delete(variantId);
  }
}

// ---------------------------------------------------------------------------
// Part 4F: Orchestrator — queued to avoid dropped calls
// ---------------------------------------------------------------------------

var _initRunning = false;
var _initQueued = false;

async function initUpsellWidgets() {
  // If already running, queue a re-run instead of silently dropping
  if (_initRunning) {
    _log('Init already running — queued re-run');
    _initQueued = true;
    return;
  }
  _initRunning = true;

  try {
    _log('--- initUpsellWidgets START ---');

    var cartData = await getCartByVendor();
    var groups = cartData.groups;
    var cartItemIds = cartData.cartItemIds;

    if (Object.keys(groups).length === 0) {
      _log('Cart empty or no vendors — removing widgets');
      document.querySelectorAll('[data-upsell-widget]').forEach(function (el) { el.remove(); });
      return;
    }

    var market = _normalizeMarket((window.noodmude && window.noodmude.market) || 'GB');
    var headroomData = await fetchShippingHeadroom(groups, market);

    if (!headroomData) {
      _warn('Headroom returned null — aborting');
      return;
    }

    if (!headroomData.vendors || Object.keys(headroomData.vendors).length === 0) {
      _warn('Headroom has no vendor data — aborting. Response:', JSON.stringify(headroomData));
      return;
    }

    var activeVendors = new Set();

    // Price cap: percentage of the most expensive item per vendor (0 = disabled)
    var priceCapPct = Number(window.noodmude && window.noodmude.upsellPriceCapPct) || 0;
    _log('Price cap %:', priceCapPct || 'disabled');

    // Excluded vendors from theme settings (line-separated textarea)
    var excludedVendorsRaw = (window.noodmude && window.noodmude.upsellExcludedVendors) || '';
    var excludedVendors = excludedVendorsRaw.split('\n')
      .map(function (v) { return v.trim().toLowerCase(); })
      .filter(function (v) { return v.length > 0; });
    if (excludedVendors.length) _log('Excluded vendors:', excludedVendors.join(', '));

    var vendorEntries = Object.entries(headroomData.vendors);
    for (var i = 0; i < vendorEntries.length; i++) {
      var vendor = vendorEntries[i][0];
      var data = vendorEntries[i][1];

      if (excludedVendors.indexOf(vendor.toLowerCase()) !== -1) {
        _log('Skipping', vendor, '— excluded in theme settings');
        continue;
      }
      // Top band is fine — the customer has committed to the highest shipping
      // price and any remaining headroom within the band ships for free.
      // Only skip when there's literally no weight budget left.
      if (data.headroomG <= 0) {
        _log('Skipping', vendor, '— no headroom (', data.headroomG, 'g, topBand:', data.isTopBand, ')');
        continue;
      }
      if (_dismissed.has(vendor)) {
        _log('Skipping', vendor, '— dismissed this session');
        continue;
      }

      // Calculate price cap for this vendor based on their most expensive cart item
      var vendorGroup = groups[vendor];
      var maxPrice = (priceCapPct > 0 && vendorGroup)
        ? vendorGroup.maxItemPrice * (priceCapPct / 100)
        : 0;
      if (maxPrice) _log('Price cap for', vendor, ':', maxPrice.toFixed(2), '(' + priceCapPct + '% of', vendorGroup.maxItemPrice.toFixed(2) + ')');

      var products = await fetchEligibleProducts(vendor, data.headroomG, cartItemIds, maxPrice, data.currency);
      if (!products.length) {
        _log('No eligible products for', vendor, '— skipping widget');
        continue;
      }

      activeVendors.add(vendor);

      var html = renderUpsellWidget(vendor, products, data.currentPrice, data.currency);
      var target = document.querySelector('[data-cart-vendor-group="' + CSS.escape(vendor) + '"]');

      if (!target) {
        _warn('Vendor group element not found for', vendor, '— drawer may not be rendered yet');
        continue;
      }

      // Remove existing widget for this vendor if present
      var existing = document.querySelector('[data-upsell-widget="' + CSS.escape(vendor) + '"]');
      if (existing) existing.remove();

      target.insertAdjacentHTML('afterend', html);
      _log('Widget injected for', vendor, '(', products.length, 'products )');
    }

    // Remove widgets for vendors no longer in cart
    document.querySelectorAll('[data-upsell-widget]').forEach(function (el) {
      var v = el.dataset.upsellWidget;
      if (!activeVendors.has(v)) {
        _log('Removing stale widget for', v);
        el.remove();
      }
    });

    initUpsellCartButtons();

    // Re-register click handler on the stable drawer slot
    _registerClickHandler();

    _log('--- initUpsellWidgets END — active:', Array.from(activeVendors).join(', ') || 'none', '---');
  } catch (err) {
    _warn('initUpsellWidgets error:', err);
  } finally {
    _initRunning = false;

    // Process queued re-run (next tick so we don't stack overflow)
    if (_initQueued) {
      _initQueued = false;
      _log('Processing queued re-run');
      setTimeout(initUpsellWidgets, 50);
    }
  }
}

// ---------------------------------------------------------------------------
// Bootstrap
// ---------------------------------------------------------------------------

// Stable wrapper — needed because 'use strict' scoping means passing the
// function reference directly to addEventListener can lose the binding.
function _onUpsellClick(e) { _handleUpsellClick(e); }

function _registerClickHandler() {
  // The Exhibit theme's cart drawer has @click.stop on #right-drawer-slot which
  // prevents clicks from bubbling to document/body. We attach our handler on
  // #right-drawer-slot (stable — NOT replaced by Alpine.morph).
  // IMPORTANT: only attach to ONE target to avoid double-firing.
  var slot = document.getElementById('right-drawer-slot');
  var target = slot || document.body;

  target.removeEventListener('click', _onUpsellClick);
  target.addEventListener('click', _onUpsellClick);
  _log('Click handler registered on', slot ? '#right-drawer-slot' : 'document.body');
}

function _scheduleInit(reason, delayMs) {
  _log('Scheduling init in', delayMs, 'ms — reason:', reason);
  setTimeout(function () {
    _registerClickHandler();
    initUpsellWidgets();
  }, delayMs);
}

function _bootstrap() {
  _log('Bootstrap — config:', JSON.stringify(window.noodmude || {}));

  if (!window.noodmude || !window.noodmude.proxyUrl) {
    _warn('Missing window.noodmude config — aborting. Check theme.liquid.');
    return;
  }

  _registerClickHandler();

  // Initial load
  initUpsellWidgets();

  // Re-run on our own add-to-cart event
  document.addEventListener('noodmude:cart:updated', function () {
    _scheduleInit('noodmude:cart:updated', 200);
  });

  // Exhibit theme: cart drawer opened — drawer may be freshly rendered
  document.body.addEventListener('cart-is-open', function () {
    _scheduleInit('cart-is-open', 100);
  });

  // Exhibit theme: item added or qty changed — Alpine.morph runs in $nextTick.
  // After morph, the [data-cart-items] DOM is replaced, destroying our widgets.
  // Wait for morph to settle then re-inject.
  window.addEventListener('switch:cart:afteradditem', function () {
    _scheduleInit('switch:cart:afteradditem', 250);
  });
  window.addEventListener('switch:modalcart:cartqtychange', function () {
    _scheduleInit('switch:modalcart:cartqtychange', 250);
  });

  // Fallback: MutationObserver watches for [data-cart-vendor-group] appearing
  // in the DOM (catches cases where the drawer renders after our initial attempt).
  var _observerDebounce = null;
  var observer = new MutationObserver(function (mutations) {
    // Only act if vendor groups are now in the DOM but no widgets exist yet
    var hasGroups = document.querySelector('[data-cart-vendor-group]');
    var hasWidgets = document.querySelector('[data-upsell-widget]');
    if (hasGroups && !hasWidgets && !_initRunning) {
      if (_observerDebounce) clearTimeout(_observerDebounce);
      _observerDebounce = setTimeout(function () {
        _log('MutationObserver: vendor groups found but no widgets — triggering init');
        _registerClickHandler();
        initUpsellWidgets();
      }, 200);
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
  _log('MutationObserver active');
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', _bootstrap);
} else {
  _bootstrap();
}
